import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { GeneratedScenario } from '../../models/wizard.models';

interface SelectableChipOption {
  id: string;
  label: string;
}

interface UploadedFileItem {
  id: string;
  name: string;
  sizeLabel: string;
}

@Component({
  selector: 'app-step-five',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './step-five.component.html',
  styleUrl: './step-five.component.scss',
})
export class StepFiveComponent {
  @Input() threatSeeds: SelectableChipOption[] = [];
  @Input() selectedThreatSeedIds!: Set<string>;
  @Input() dragOver = false;
  @Input() uploadedFiles: UploadedFileItem[] = [];
  @Input() scenarioDescription = '';
  @Input() scenariosGenerated = false;
  @Input() generationError = '';
  @Input() isGenerating = false;
  @Input() generatedScenarios: GeneratedScenario[] = [];
  @Input() selectedScenarioId: string | null = null;
  @Input() noFeedbackNeeded = false;
  @Input() feedbackComment = '';

  isThreatSeedSelected(threatSeedId: string): boolean {
    return this.selectedThreatSeedIds.has(threatSeedId);
  }

  toggleThreatSeed(threatSeedId: string): void {
    if (this.selectedThreatSeedIds.has(threatSeedId)) {
      this.selectedThreatSeedIds.delete(threatSeedId);
      return;
    }

    this.selectedThreatSeedIds.add(threatSeedId);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;

    const files = event.dataTransfer?.files;

    if (!files || files.length === 0) {
      return;
    }

    const validFiles = Array.from(files).filter((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      return extension === 'pdf' || extension === 'doc' || extension === 'docx';
    });

    this.processFiles(validFiles);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (!files || files.length === 0) {
      return;
    }

    const validFiles = Array.from(files).filter((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      return extension === 'pdf' || extension === 'doc' || extension === 'docx';
    });

    this.processFiles(validFiles);
    input.value = '';
  }

  private processFiles(files: File[]): void {
    const newFiles = files.map((file) => ({
      id: `file-${Date.now()}-${Math.random().toString(36).substring(7)}`,
      name: file.name,
      sizeLabel: this.formatFileSize(file.size),
    }));

    this.uploadedFiles = [...this.uploadedFiles, ...newFiles];
  }

  removeFile(fileId: string): void {
    this.uploadedFiles = this.uploadedFiles.filter(
      (file) => file.id !== fileId,
    );
  }

  updateScenarioDescription(event: Event): void {
    this.scenarioDescription = (event.target as HTMLTextAreaElement).value;
  }

  selectScenario(scenarioId: string): void {
    this.selectedScenarioId = scenarioId;
  }

  isScenarioSelected(scenarioId: string): boolean {
    return this.selectedScenarioId === scenarioId;
  }

  toggleNoFeedback(event: Event): void {
    this.noFeedbackNeeded = (event.target as HTMLInputElement).checked;
  }

  updateFeedback(event: Event): void {
    this.feedbackComment = (event.target as HTMLTextAreaElement).value;
  }

  private formatFileSize(sizeInBytes: number): string {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    }

    if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    }

    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
